import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, ExternalLink, Mail, Linkedin, Clock, User, Star, Shield, MessageSquare, Send, Users, UserCheck } from "lucide-react";
import { ContactDetailModal } from "@/components/contacts/ContactDetailModal";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { openExternal } from "@/lib/utils";
import { AccountStatusBadge, BuyingModeBadge } from "./StatusBadge";
import type { TargetUniverseAccount, AccountActivity, EngagementEvent } from "@/hooks/useTargetUniverse";
import {
  useUpdateAccount,
  useAccountActivities,
  useAccountTopContacts,
  useAccountEngagementHistory,
  useTeamReps,
} from "@/hooks/useTargetUniverse";
import { useAuth } from "@/contexts/AuthContext";
import { AgentVerdictBadge } from "@/components/accounts/AgentVerdictBadge";

interface AccountReviewPanelProps {
  account: TargetUniverseAccount | null;
  onClose: () => void;
}

const ACTIVITY_ICONS: Record<string, typeof Clock> = {
  reviewed: Clock,
  status_changed: Star,
  tier_changed: Star,
  note_added: MessageSquare,
  contact_added_to_campaign: Mail,
  opportunity_created: Shield,
  assigned: User,
  disqualified: X,
  reactivated: Star,
};

export function AccountReviewPanel({ account, onClose }: AccountReviewPanelProps) {
  const navigate = useNavigate();
  const updateAccount = useUpdateAccount();
  const { userProfile } = useAuth();
  const performedBy = {
    id: userProfile?.id ?? null,
    name: userProfile?.sales_rep_name || userProfile?.full_name || "User",
  };

  // Contact Intelligence modal state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalId, setContactModalId] = useState<string | null>(null);
  const { data: activities } = useAccountActivities(account?.id || null);
  const { data: topContacts } = useAccountTopContacts(account?.id || null);
  const { data: engagementHistory } = useAccountEngagementHistory(account?.id || null);
  const { data: teamReps } = useTeamReps();

  // Account Status & Assignment
  const [tier, setTier] = useState<string>("unreviewed");
  const [assignedTo, setAssignedTo] = useState("");
  const [notes, setNotes] = useState("");
  const [qualReason, setQualReason] = useState("");
  // Preparing for Pipeline
  const [buyingMode, setBuyingMode] = useState<string>("");
  const [budgetConfirmed, setBudgetConfirmed] = useState(false);
  const [fitConfirmed, setFitConfirmed] = useState(false);
  const [warmIntro, setWarmIntro] = useState(false);
  const [champion, setChampion] = useState(false);

  useEffect(() => {
    if (account) {
      setTier(account.account_tier || "unreviewed");
      setAssignedTo(account.targeting_assigned_to || "");
      setNotes(account.human_summary || "");
      setQualReason(account.qualification_reason || "");
      setBuyingMode(account.buying_mode || "");
      setBudgetConfirmed(account.budget_confirmed || false);
      setFitConfirmed(account.fit_confirmed || false);
      setWarmIntro(account.warm_intro_available || false);
      setChampion(account.champion_identified || false);
    }
  }, [account]);

  if (!account) return null;

  const pipelineGateMet =
    buyingMode && ["growth", "trouble", "overconfident"].includes(buyingMode) &&
    budgetConfirmed && fitConfirmed && warmIntro && champion;

  const handleSave = async () => {
    const oldTier = account.account_tier || "unreviewed";

    // Require qualification_reason when disqualifying
    if (tier === "disqualified" && !qualReason.trim()) {
      toast.error("Disqualification reason required", {
        description: "Please explain why this account should be disqualified before saving.",
      });
      return;
    }

    try {
      await updateAccount.mutateAsync({
        accountId: account.id,
        fields: {
          warm_intro_available: warmIntro,
          champion_identified: champion,
          targeting_assigned_to: assignedTo || null,
          targeting_notes: notes || null,
          qualification_reason: qualReason || null,
          last_reviewed_at: new Date().toISOString(),
          last_reviewed_by: performedBy.name,
          account_tier: tier,
          buying_mode: buyingMode || null,
          budget_confirmed: budgetConfirmed,
          fit_confirmed: fitConfirmed,
          ...(oldTier !== tier
            ? { tier_changed_at: new Date().toISOString(), tier_changed_by: performedBy.name }
            : {}),
        },
        activityType: oldTier !== tier ? "tier_changed" : "reviewed",
        activityDescription:
          oldTier !== tier
            ? `Tier changed from ${oldTier} to ${tier}`
            : `Account reviewed`,
        activityMetadata: {
          old_tier: oldTier,
          new_tier: tier,
        },
        performedBy,
      });
      toast.success("Account updated", { description: `${account.name} saved successfully.` });
    } catch (err) {
      toast.error("Failed to update account");
    }
  };

  const handleMarkReviewed = async () => {
    try {
      await updateAccount.mutateAsync({
        accountId: account.id,
        fields: {
          last_reviewed_at: new Date().toISOString(),
          last_reviewed_by: performedBy.name,
        },
        activityType: "reviewed",
        activityDescription: "Account reviewed",
        performedBy,
      });
      toast.success("Marked as reviewed");
    } catch {
      toast.error("Error");
    }
  };

  return (
    <div className="fixed inset-y-0 right-0 w-[480px] bg-background border-l shadow-xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold truncate">{account.name}</h2>
          <div className="flex items-center gap-2 mt-1">
            <AccountStatusBadge status={account.account_tier} />
            {account.icp_tier && (
              <Badge variant="outline" className="text-xs">
                {account.icp_tier}{account.icp_score ? ` (${account.icp_score})` : ""}
              </Badge>
            )}
            {account.domain && (
              <a
                href={`https://${account.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                {account.domain} <ExternalLink className="h-3 w-3" />
              </a>
            )}
            <Button
              variant="link"
              size="sm"
              className="text-xs h-auto p-0"
              onClick={() => navigate(`/accounts/${account.id}`)}
            >
              View Full Account →
            </Button>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Agent verdict badge (HITL) */}
          <AgentVerdictBadge companyId={account.id} />

          {/* Intelligence Summary */}
          <section>
            <h3 className="text-sm font-semibold mb-2">Intelligence Summary</h3>
            <div className="text-sm text-muted-foreground space-y-1">
              {account.description ? (
                <p className="line-clamp-4">{account.description}</p>
              ) : (
                <p className="italic">No description available</p>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                {account.primary_category && (
                  <span className="text-xs bg-muted px-2 py-0.5 rounded">
                    {account.primary_category.replace(/_/g, " ")}
                  </span>
                )}
                {account.development_stage && (
                  <span className="text-xs bg-muted px-2 py-0.5 rounded">{account.development_stage}</span>
                )}
                {account.headquarters_state && (
                  <span className="text-xs bg-muted px-2 py-0.5 rounded">
                    {account.headquarters_city ? `${account.headquarters_city}, ` : ""}
                    {account.headquarters_state}
                  </span>
                )}
                {account.territory && (
                  <span className="text-xs bg-muted px-2 py-0.5 rounded">
                    {account.territory}
                  </span>
                )}
                {account.modalities?.map((m: string) => (
                  <span key={m} className="text-xs bg-muted px-2 py-0.5 rounded">
                    {m}
                  </span>
                ))}
              </div>
            </div>
          </section>

          <Separator />

          {/* Top Contacts */}
          <section>
            <h3 className="text-sm font-semibold mb-2">
              Top Contacts ({account.contact_count})
            </h3>
            {(topContacts || []).length > 0 ? (
              <div className="space-y-2">
                {(topContacts as any[]).map((c: any) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between text-sm border rounded-md p-2 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => { setContactModalId(c.id); setContactModalOpen(true); }}
                  >
                    <div>
                      <div className="font-medium flex items-center gap-1">
                        {c.first_name} {c.last_name}
                        <UserCheck className="h-3 w-3 text-muted-foreground" />
                      </div>
                      <div className="text-xs text-muted-foreground">{c.job_title || "—"}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      {c.email && (
                        <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()}>
                          <Mail className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                        </a>
                      )}
                      {c.linkedin_url && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); openExternal(c.linkedin_url); }}>
                          <Linkedin className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">No contacts found</p>
            )}
          </section>

          <Separator />

          {/* Account Status & Assignment */}
          <section className="space-y-4">
            <h3 className="text-sm font-semibold">Account Status & Assignment</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Account Status</Label>
                <Select value={tier} onValueChange={setTier}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unreviewed">Unreviewed</SelectItem>
                    <SelectItem value="cold">Cold</SelectItem>
                    <SelectItem value="validated_target">Validated Target</SelectItem>
                    <SelectItem value="nurture">Nurture</SelectItem>
                    <SelectItem value="pipeline_ready">Pipeline Ready</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="disqualified">Disqualified</SelectItem>
                    <SelectItem value="parked">Parked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Assigned To</Label>
                <Select value={assignedTo} onValueChange={setAssignedTo}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select rep" />
                  </SelectTrigger>
                  <SelectContent>
                    {(teamReps || []).map((rep) => (
                      <SelectItem key={rep.first_name} value={rep.first_name}>{rep.first_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {tier === "disqualified" && (
              <div>
                <Label className="text-xs">Disqualification Reason</Label>
                <Textarea
                  value={qualReason}
                  onChange={(e) => setQualReason(e.target.value)}
                  className="mt-1"
                  rows={2}
                  placeholder="Why is this account disqualified?"
                />
              </div>
            )}
          </section>

          <Separator />

          {/* Preparing for Pipeline */}
          <section className="space-y-4">
            <h3 className="text-sm font-semibold">Preparing for Pipeline</h3>
            <p className="text-[11px] text-muted-foreground -mt-2">
              Check off criteria as you qualify this account. Pipeline gate is met when all are satisfied.
            </p>

            <div>
              <Label className="text-xs">Buying Mode</Label>
              <Select value={buyingMode} onValueChange={setBuyingMode}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Set mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="growth">Growth</SelectItem>
                  <SelectItem value="trouble">Trouble</SelectItem>
                  <SelectItem value="even_keel">Even Keel</SelectItem>
                  <SelectItem value="overconfident">Overconfident</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={budgetConfirmed} onCheckedChange={setBudgetConfirmed} id="budget" />
                <Label htmlFor="budget" className="text-xs">Budget Confirmed</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={fitConfirmed} onCheckedChange={setFitConfirmed} id="fit" />
                <Label htmlFor="fit" className="text-xs">Fit Confirmed</Label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={warmIntro} onCheckedChange={setWarmIntro} id="warm-intro" />
                <Label htmlFor="warm-intro" className="text-xs">Warm Intro Available</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={champion} onCheckedChange={setChampion} id="champion" />
                <Label htmlFor="champion" className="text-xs">Champion Identified</Label>
              </div>
            </div>

            {pipelineGateMet && (
              <div className="rounded-md bg-green-50 border border-green-200 p-3 text-center">
                <p className="text-sm font-semibold text-green-700">Pipeline Gate Met</p>
                <p className="text-xs text-green-600 mt-0.5">This account qualifies for pipeline entry</p>
              </div>
            )}
          </section>

          <Separator />

          {/* Engagement History */}
          <section>
            <h3 className="text-sm font-semibold mb-2">Engagement History</h3>
            {(engagementHistory || []).length > 0 ? (
              <div className="space-y-2">
                {(engagementHistory || []).slice(0, 8).map((evt: EngagementEvent) => {
                  const Icon = evt.type === "outreach" ? Send : Users;
                  return (
                    <div key={evt.id} className="flex items-start gap-2 text-sm">
                      <Icon className="h-3.5 w-3.5 mt-0.5 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-foreground text-xs">{evt.title}</p>
                        <div className="flex items-center gap-1.5">
                          {evt.status && (
                            <Badge variant="outline" className="text-[10px] h-4 px-1">
                              {evt.status}
                            </Badge>
                          )}
                          {evt.detail && (
                            <span className="text-[10px] text-muted-foreground">{evt.detail}</span>
                          )}
                          <span className="text-[10px] text-muted-foreground">
                            {new Date(evt.date).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-4">
                <Send className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                <p className="text-sm text-muted-foreground">No engagement history yet</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Outreach messages and campaign enrollments will appear here as campaigns launch
                </p>
              </div>
            )}
          </section>

          <Separator />

          {/* Notes */}
          <section>
            <h3 className="text-sm font-semibold mb-2">Notes</h3>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Rep notes, observations, context..."
            />
          </section>

          <Separator />

          {/* Recent Activity */}
          <section>
            <h3 className="text-sm font-semibold mb-2">Recent Activity</h3>
            {(activities || []).length > 0 ? (
              <div className="space-y-2">
                {(activities || []).slice(0, 5).map((act: AccountActivity) => {
                  const Icon = ACTIVITY_ICONS[act.activity_type] || Clock;
                  return (
                    <div key={act.id} className="flex items-start gap-2 text-sm">
                      <Icon className="h-3.5 w-3.5 mt-0.5 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-foreground">{act.description || act.activity_type.replace(/_/g, " ")}</p>
                        <p className="text-xs text-muted-foreground">
                          {act.performed_by && `${act.performed_by} · `}
                          {new Date(act.created_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">No activity yet</p>
            )}
          </section>
        </div>
      </ScrollArea>

      {/* Contact Detail Modal */}
      <ContactDetailModal
        contactId={contactModalId}
        open={contactModalOpen}
        onOpenChange={setContactModalOpen}
      />

      {/* Footer Actions */}
      <div className="p-4 border-t flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={handleMarkReviewed}>
          Mark Reviewed
        </Button>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} disabled={updateAccount.isPending}>
          {updateAccount.isPending ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}
